import { Link } from "react-router-dom";
import { CABINET, COMPETENCES } from "@/lib/content";
import { MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import { useSiteSettings } from "@/lib/useSiteSettings";

export default function Footer() {
    const { settings } = useSiteSettings();
    const phone = settings.phone || CABINET.phone;
    const phoneIntl = settings.phone_intl || CABINET.phoneIntl;
    const email = settings.email || CABINET.email;
    const street = settings.address_street || CABINET.address;
    const city = settings.address_city || CABINET.city;
    const canton = settings.canton || CABINET.canton;
    return (
        <footer
            data-testid="site-footer"
            style={{
                background: "var(--forest-deep)",
                color: "var(--cream)",
                marginTop: 120,
            }}
        >
            <div
                className="mx-auto"
                style={{ maxWidth: 1400, padding: "80px 40px 32px" }}
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
                    {/* Brand */}
                    <div className="md:col-span-4">
                        <img
                            src="/assets/emblem.png"
                            alt="ANA Corps & Souffle"
                            style={{
                                height: 60,
                                filter: "brightness(0) invert(1) opacity(0.9)",
                            }}
                        />
                        <div
                            className="font-display mt-4"
                            style={{ fontSize: 26, color: "var(--cream)" }}
                        >
                            ANA Corps & Souffle
                        </div>
                        <div
                            className="mt-3"
                            style={{
                                fontSize: 14,
                                color: "var(--terra-light)",
                                fontStyle: "italic",
                                lineHeight: 1.6,
                                maxWidth: 320,
                            }}
                        >
                            Cabinet de physiothérapie. Ton clinique, sobre, factuel — au
                            service des patients référés par leur médecin.
                        </div>
                    </div>

                    {/* Coordonnées */}
                    <div className="md:col-span-3">
                        <div className="font-mono-clinical" style={{ color: "var(--terra-light)" }}>
                            Coordonnées
                        </div>
                        <div className="divider-terra mt-3" style={{ background: "rgba(255,255,255,0.08)" }} />
                        <div className="mt-5 space-y-4">
                            <div className="flex items-start gap-3">
                                <MapPin size={16} style={{ marginTop: 3, color: "var(--terra-light)" }} />
                                <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                                    {street}<br />
                                    {city}<br />
                                    <span style={{ color: "var(--terra-light)" }}>{canton}</span>
                                </div>
                            </div>
                            <a
                                href={`tel:${phoneIntl}`}
                                data-testid="footer-phone"
                                className="flex items-center gap-3 link-underline"
                                style={{ color: "var(--cream)", fontSize: 14 }}
                            >
                                <Phone size={14} style={{ color: "var(--terra-light)" }} />
                                {phone}
                            </a>
                            <a
                                href={`mailto:${email}`}
                                data-testid="footer-email"
                                className="flex items-center gap-3 link-underline"
                                style={{ color: "var(--cream)", fontSize: 14 }}
                            >
                                <Mail size={14} style={{ color: "var(--terra-light)" }} />
                                {email}
                            </a>
                        </div>
                    </div>

                    {/* Navigation */}
                    <div className="md:col-span-2">
                        <div className="font-mono-clinical" style={{ color: "var(--terra-light)" }}>
                            Navigation
                        </div>
                        <div className="divider-terra mt-3" style={{ background: "rgba(255,255,255,0.08)" }} />
                        <ul className="mt-5 space-y-3" style={{ listStyle: "none", padding: 0 }}>
                            {[
                                ["Accueil", "/"],
                                ["Domaines", "/domaines"],
                                ["Le cabinet", "/cabinet"],
                                ["Journal", "/journal"],
                                ["Rendez-vous", "/rendez-vous"],
                                ["Contact", "/contact"],
                            ].map(([label, to]) => (
                                <li key={to}>
                                    <Link
                                        to={to}
                                        className="link-underline"
                                        style={{ color: "var(--cream)", fontSize: 14 }}
                                    >
                                        {label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Compétences */}
                    <div className="md:col-span-3">
                        <div className="font-mono-clinical" style={{ color: "var(--terra-light)" }}>
                            Compétences cliniques
                        </div>
                        <div className="divider-terra mt-3" style={{ background: "rgba(255,255,255,0.08)" }} />
                        <ul
                            className="mt-5 grid grid-cols-1 gap-2"
                            style={{ listStyle: "none", padding: 0 }}
                        >
                            {COMPETENCES.map((c) => (
                                <li
                                    key={c}
                                    style={{
                                        fontSize: 13,
                                        color: "var(--cream)",
                                        opacity: 0.85,
                                    }}
                                >
                                    · {c}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Bottom bar */}
                <div
                    className="mt-16 pt-8 flex flex-col md:flex-row justify-between gap-4"
                    style={{
                        borderTop: "1px solid rgba(255,255,255,0.08)",
                        fontSize: 12,
                        color: "var(--terra-light)",
                    }}
                >
                    <div>
                        © {new Date().getFullYear()} ANA Corps & Souffle — Consultation sur rendez-vous
                    </div>
                    <div className="flex gap-6 items-center">
                        <span style={{ fontStyle: "italic" }}>Bex · Canton de Vaud · Suisse</span>
                        <Link
                            to="/medecin"
                            data-testid="footer-referrer-link"
                            className="link-underline"
                            style={{ color: "var(--terra-light)", opacity: 0.65 }}
                        >
                            Espace médecin <ArrowUpRight size={11} style={{ display: "inline" }} />
                        </Link>
                        <Link
                            to="/admin"
                            data-testid="footer-admin-link"
                            className="link-underline"
                            style={{ color: "var(--terra-light)", opacity: 0.6 }}
                        >
                            Espace praticien <ArrowUpRight size={11} style={{ display: "inline" }} />
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
